import { EventImpl } from "@fullcalendar/core/internal";
import FullCalendar from "@fullcalendar/react";
import { RefObject, useEffect, useMemo, useRef } from "react";
import { interactiveCalendarProps } from "@/components/common/calendar/interactiveCalendarProps";
import MoxieFullCalendar from "@/components/common/calendar/moxieFullCalendar";
import InfoCard from "@/components/common/infoCard/infoCard";
import { Provider } from "@/components/common/providerSection";
import { useProvidersWorkingHoursQuery } from "@/graphql/queries/workingHours.graphql.types";
import useCreateViewCalendarEvents from "@/hooks/appointments/useCreateViewCalendarEvents";
import useCalendarGoToDate from "@/hooks/calendar/useCalendarGoToDate";
import { mapAvailabilityToBusinessHours } from "@/hooks/common/useBusinessHours";
import {
  DEFAULT_CALENDAR_HEADER_HEIGHT,
  DEFAULT_PAGE_PADDING_BOTTOM,
  getCalendarHeight,
} from "@/utils";
import { ClientForAppointmentCreation } from "../../common/clientSection";
import { AvailableService } from "./appointmentServicesSection";
import { AppointmentServiceInput } from "./multiProviderAppointmentDrawerContext";

type Props = {
  appointmentId?: string;
  date: Date;
  appointmentServices: AppointmentServiceInput[];
  providers: Provider[];
  availableServices: AvailableService[];
  client: ClientForAppointmentCreation;
  onDateChange: (date: Date) => void;
  onAppointmentTimeChange: (options: {
    newStartTime: Date;
    newEndTime: Date;
  }) => void;
  calendarContainerRef: RefObject<HTMLDivElement>;
};

const CALENDAR_HEADER_HEIGHT = 68;

export const TimeSelectionCalendar = ({
  appointmentId,
  date,
  appointmentServices,
  providers,
  availableServices,
  client,
  onDateChange,
  onAppointmentTimeChange,
  calendarContainerRef,
}: Props) => {
  const calendarRef = useRef<FullCalendar | null>(null);
  const { goToDate } = useCalendarGoToDate(calendarRef);

  useEffect(() => {
    const calendarDate = calendarRef.current?.getApi().getDate();
    if (calendarDate !== date) {
      goToDate(date);
    }
  }, [date, goToDate]);

  const handleCalendarGoToDate = (calendarNewDate: Date) => {
    goToDate(calendarNewDate);
    const updatedDate = calendarRef.current?.getApi().getDate();

    if (!updatedDate) {
      return;
    }
    onDateChange(calendarNewDate);
  };

  const handleUpdateAppointmentEvent = (event: EventImpl) => {
    const { start, end } = event;

    onAppointmentTimeChange({
      newStartTime: start,
      newEndTime: end,
    });
  };

  const businessHours = useCalendarBusinessHours({
    appointmentServices,
  });

  const appointmentStartTime = appointmentServices[0].startTime;
  const appointmentEndTime = appointmentServices.at(-1).endTime;

  const calendarEvents = useCreateViewCalendarEvents({
    start: appointmentStartTime,
    end: appointmentEndTime,
    providers,
    availableServices,
    client,
    excludeAppointmentId: appointmentId,
    currentAppointmentServices: appointmentServices,
  });

  return (
    <InfoCard sx={{ mt: 2, zIndex: 9999 }}>
      <MoxieFullCalendar
        calendarRef={calendarRef}
        handleCalendarGoToDate={handleCalendarGoToDate}
        height={getCalendarHeight(
          CALENDAR_HEADER_HEIGHT,
          DEFAULT_CALENDAR_HEADER_HEIGHT,
          DEFAULT_PAGE_PADDING_BOTTOM,
          0
        )}
        initDate={date}
        initialView="timeGridDay"
        {...interactiveCalendarProps}
        events={calendarEvents}
        eventChange={(info) => {
          handleUpdateAppointmentEvent(info.event);
        }}
        eventAdd={(info) => {
          // When we drag event fire it sometimes the event moves by 5 minutes on it's own.
          // In this case if we drop it without moving it, eventChange is not triggered, it is treated as eventAdd instead.
          // This is because in eventDragStart callback we update the state and in effect MoxieFullCalendar businessHours prop gets updated.
          // Inside fullcalendar an internal context object changes, which affects handleDragEnd callback.
          // Specifically this condition doesn't match:
          // https://github.com/fullcalendar/fullcalendar/blob/v6.1.15/packages/interaction/src/interactions/EventDragging.ts#L267
          // Because we don't add or remove events in this component, we can safely use eventAdd just like eventChange.
          handleUpdateAppointmentEvent(info.event);
        }}
        businessHours={businessHours}
        calendarContainerRef={calendarContainerRef}
        focusOnTime={date}
      />
    </InfoCard>
  );
};

const useCalendarBusinessHours = ({
  appointmentServices,
}: {
  appointmentServices: AppointmentServiceInput[];
}) => {
  const { data: workingHoursData } = useProvidersWorkingHoursQuery({
    variables: {
      providerIds: appointmentServices.map(({ provider }) => provider.id),
    },
    skip: !appointmentServices.length,
  });

  return useMemo(() => {
    if (!workingHoursData) {
      return [];
    }

    return workingHoursData.recurringAvailability.map((availability) => ({
      providerId: availability.providerId,
      ...mapAvailabilityToBusinessHours(availability),
    }));
  }, [workingHoursData]);
};
